import { useState, useEffect, useCallback } from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions, doc, onSnapshot, getDoc } from '../firebase';
import i18n from '../i18n/i18n';

const ensureProfileFn = httpsCallable(functions, 'ensureUserProfile');

/** When Firestore has no users/{uid} yet and ensureUserProfile fails (e.g. function not deployed), still show the app from Auth + token claims. */
async function buildUserFromAuthOnly(fu) {
  const token = await fu.getIdTokenResult(true);
  const claims = token.claims;
  const role = claims.admin === true ? 'admin' : 'user';
  return {
    _id: fu.uid,
    uid: fu.uid,
    publicId: '',
    name: fu.displayName || (fu.email && fu.email.split('@')[0]) || 'User',
    email: fu.email || '',
    phone: '',
    avatarUrl: fu.photoURL || '',
    role,
    frozen: false,
    profileScore: 0,
    preferredLanguage: '',
    notifyPrefs: null,
    appSettings: null,
  };
}

function mapProfile(uid, data) {
  if (!data) return null;
  return {
    _id: uid,
    uid,
    publicId: data.publicId || '',
    name: data.name || '',
    email: data.email || '',
    phone: data.phone || '',
    avatarUrl: data.avatarUrl || '',
    role: data.role || 'user',
    frozen: !!data.frozen,
    profileScore: data.profileScore ?? 0,
    preferredLanguage: data.preferredLanguage || '',
    notifyPrefs: data.notifyPrefs && typeof data.notifyPrefs === 'object' ? data.notifyPrefs : null,
    appSettings: data.appSettings && typeof data.appSettings === 'object' ? data.appSettings : null,
  };
}

export function useFirebaseAuth() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  /** Firebase Auth session (resolved on first `onAuthStateChanged`). Profile may still be loading. */
  const [authUid, setAuthUid] = useState(null);
  const [authInitialized, setAuthInitialized] = useState(false);

  const applyProfile = useCallback(async (fu, data) => {
    if (!data) return;
    if (data.frozen) {
      await signOut(auth);
      setUser(null);
      return;
    }
    const profile = mapProfile(fu.uid, data);
    setUser(profile);
    const pl = data.preferredLanguage;
    if (pl && typeof pl === 'string') {
      const code = pl.split('-')[0].toLowerCase();
      if (['en', 'de', 'ar', 'ku'].includes(code)) {
        void i18n.changeLanguage(code);
      }
    }
  }, []);

  useEffect(() => {
    let unsubProfile = () => {};
    const unsubAuth = onAuthStateChanged(auth, (fu) => {
      unsubProfile();
      setAuthUid(fu ? fu.uid : null);
      setAuthInitialized(true);
      if (!fu) {
        setUser(null);
        setLoading(false);
        return;
      }
      setLoading(true);
      const userRef = doc(db, 'users', fu.uid);
      unsubProfile = onSnapshot(
        userRef,
        async (snap) => {
          try {
            if (!snap.exists()) {
              try {
                await ensureProfileFn();
              } catch (e) {
                console.error(e);
                try {
                  setUser(await buildUserFromAuthOnly(fu));
                } catch (e2) {
                  console.error(e2);
                  setUser(null);
                }
                setLoading(false);
              }
              return;
            }
            const data = snap.data();
            if (!data) {
              setLoading(false);
              return;
            }
            await applyProfile(fu, data);
          } finally {
            if (snap.exists()) {
              setLoading(false);
            }
          }
        },
        (err) => {
          console.error(err);
          setLoading(false);
          setUser(null);
        }
      );
    });
    return () => {
      unsubAuth();
      unsubProfile();
    };
  }, [applyProfile]);

  const refreshUser = useCallback(async () => {
    const fu = auth.currentUser;
    if (!fu) {
      setUser(null);
      return;
    }
    const snap = await getDoc(doc(db, 'users', fu.uid));
    const data = snap.data();
    if (data) await applyProfile(fu, data);
  }, [applyProfile]);

  const login = async (email, password) => {
    await signInWithEmailAndPassword(auth, email, password);
  };

  const logout = async () => {
    await signOut(auth);
    setUser(null);
  };

  return {
    user,
    loading,
    authUid,
    authInitialized,
    refreshUser,
    login,
    logout,
  };
}
